// src/roles/employee/pages/CompletedTasks.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./CompletedTasks.css";

export default function CompletedTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8; // 👈 change this for more/less per page

  const navigate = useNavigate();

  function parseToken() {
    try {
      const token = localStorage.getItem("token");
      if (!token) return null;
      const parts = token.split(".");
      if (parts.length !== 3) return null;
      const payload = JSON.parse(
        atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
      );
      return payload;
    } catch (err) {
      return null;
    }
  }

  const tokenPayload = parseToken();
  const localUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch (e) {
      return {};
    }
  })();

  const employeeId =
    tokenPayload?.employee_id ??
    (tokenPayload?.role === "employee" ? tokenPayload?.id : null) ??
    localUser?.employee_id ??
    null;

  function apiHeaders(json = true) {
    const headers = {};
    if (json) headers["Content-Type"] = "application/json";
    const token = localStorage.getItem("token");
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
  }

  async function loadCompletedTasks() {
    if (!employeeId) {
      setTasks([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/tasks/my`, {
        method: "GET",
        headers: apiHeaders(false),
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "{}");
        throw new Error(txt || "Failed to load tasks");
      }
      const body = await res.json();
      const arr = Array.isArray(body) ? body : [];

      const normalized = arr.map((t) => {
        const s = (t.status || "").toString().toLowerCase();
        const map = {
          todo: "todo",
          pending: "todo",
          to_do: "todo",
          in_progress: "in_progress",
          "in-progress": "in_progress",
          progress: "in_progress",
          done: "done",
          completed: "done",
          complete: "done",
        };
        t._col = map[s] || "todo";

        // if assignment status exists for this employee, use it
        t._myAssignment = null;
        if (Array.isArray(t.assignments) && t.assignments.length) {
          const myId = Number(
            tokenPayload?.employee_id ??
              tokenPayload?.id ??
              localUser?.employee_id
          );
          if (!Number.isNaN(myId)) {
            const found = t.assignments.find(
              (a) => Number(a.employee_id) === myId
            );
            if (found) {
              t._myAssignment = found;
              const as = (found.status || "").toString().toLowerCase();
              t._col = as === "done" || as === "completed" ? "done" : t._col;
            }
          }
        }

        return t;
      });

      const doneTasks = normalized.filter((t) => {
        if ((t._col || "").toLowerCase() === "done") return true;
        const st = (t.status || "").toLowerCase();
        if (st === "done" || st === "completed") return true;
        const a = t._myAssignment;
        if (a && (a.status || "").toLowerCase() === "done") return true;
        return false;
      });

      doneTasks.sort((a, b) => {
        const aTime = a.updated_at
          ? new Date(a.updated_at).getTime()
          : a.id || 0;
        const bTime = b.updated_at
          ? new Date(b.updated_at).getTime()
          : b.id || 0;
        return bTime - aTime;
      });

      setTasks(doneTasks);
      setCurrentPage(1); // reset to page 1 on reload
    } catch (err) {
      console.error("loadCompletedTasks error:", err);
      setError(err.message || "Failed to load completed tasks");
      setTasks([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadCompletedTasks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  // === PAGINATION ===
  const totalPages = Math.ceil(tasks.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentTasks = tasks.slice(startIndex, startIndex + itemsPerPage);

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="employee-home" style={{ padding: 24 }}>
      <header className="eh-header" style={{ marginBottom: 12 }}>
        <div>
          <h2>Completed Tasks</h2>
          <p className="eh-sub">All tasks you completed (history).</p>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button className="eh-btn" onClick={() => navigate(-1)}>
            ← Back
          </button>
          <button
            className="eh-btn"
            onClick={() => {
              loadCompletedTasks();
            }}
            disabled={loading}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </header>

      {error && <div className="eh-message error">{error}</div>}

      <main>
        {loading ? (
          <div>Loading…</div>
        ) : tasks.length === 0 ? (
          <div style={{ color: "#666" }}>No completed tasks found.</div>
        ) : (
          <>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {currentTasks.map((t) => (
                <li
                  key={t.id}
                  className="eh-card"
                  style={{ marginBottom: 12, cursor: "default" }}
                >
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <div>
                      <div style={{ fontWeight: 700 }}>{t.title}</div>
                      <div style={{ fontSize: 13, color: "#334155" }}>
                        {t.project_name || `Project ${t.project_id}`}{" "}
                        {t.milestone_title ? `• ${t.milestone_title}` : ""}
                      </div>
                    </div>
                    <div
                      style={{
                        textAlign: "right",
                        fontSize: 12,
                        color: "#8898aa",
                      }}
                    >
                      Completed:{" "}
                      {t.updated_at
                        ? new Date(t.updated_at).toLocaleString()
                        : "—"}
                      <div style={{ marginTop: 6 }}>
                        Time: {t.time_spent != null ? t.time_spent + "h" : "0h"}
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 13 }}>
                    {t.description || ""}
                  </div>
                </li>
              ))}
            </ul>

            {/* === PAGINATION FOOTER === */}
            {totalPages > 1 && (
              <div
                className="pagination"
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: 10,
                  marginTop: 20,
                  paddingTop: 10,
                  borderTop: "1px solid #e5e7eb",
                }}
              >
                <button
                  className="eh-btn"
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  ← Prev
                </button>
                <span
                  style={{
                    fontSize: "0.9rem",
                    color: "#1e2a78",
                    fontWeight: 600,
                  }}
                >
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className="eh-btn"
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
